// theme.js
import { createTheme } from "@mui/material/styles";

const lightTheme = createTheme({
  typography: {
    fontFamily: "Arial, sans-serif",
    h1: {
      fontSize: "2.5rem", // 40px
      margin: "0.5em 0",
      [`@media (max-width:600px)`]: {
        fontSize: "2.2rem", // ~33px
      },
    },
    h2: {
      fontSize: "2rem", // 32px
      margin: "0.5em 0",
      [`@media (max-width:600px)`]: {
        fontSize: "1.8rem", // ~27px
      },
    },
    h3: {
      fontSize: "2rem", // Course name on mainpage
      margin: "0.5em 0",
      [`@media (max-width:600px)`]: {
        fontSize: "1.5rem", // ~22.5px
      },
    },
    h4: {
      fontSize: "1.5rem", //CourseName
      margin: "0.5em 0",
      [`@media (max-width:600px)`]: {
        fontSize: "1.25rem", // ~20px mobile
      },
    },
    h5: {
      fontSize: "1.1rem", //  Module Names
      margin: "0.5em 0",
      [`@media (max-width:600px)`]: {
        fontSize: "1rem", // ~15.2px mobile
      },
    },
    h6: {
      fontSize: "0.89rem", //  lesson Names
      margin: "0.5em 0",
      [`@media (max-width:600px)`]: {
        fontSize: "0.89rem", // ~13.6px mobile
      },
    },

    body1: {
      fontSize: "1rem",
    },
    body2: {
      fontSize: "0.875rem", // description and other small fonts
    },
  },

  palette: {
    mode: "light",
    primary: {
      main: "#1976d2", // Custom primary color for light mode
    },
    secondary: {
      main: "#d32f2f", // Custom secondary color for light mode
    },
    grey: {
      main: "#9e9e9e", // or any shade you prefer
      contrastText: "#fff",
    },
    white: {
      main: "#ffffff", // Pure white
      contrastText: "#000000", // Black text for best contrast
    },

    background: {
      default: "#ffffff", // Background color for light mode
      paper: "#ffffff", // Paper background color for light mode
    },
    text: {
      primary: "#000000", // Text color for light mode
      secondary: "#555", // Secondary text color for light mode
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: "16px",
          "@media (max-width:600px)": {
            fontSize: "15px",
          },
        },
        body: {
          margin: 0,
          padding: 0,
        },
      },
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        color: "primary",
      },
      styleOverrides: {
        root: {
          borderRadius: 3,
          padding: "8px 16px",
          textTransform: "none",
        },
        containedPrimary: {
          backgroundColor: "#1976d2",
          color: "#fff",
        },
        containedSecondary: {
          backgroundColor: "#d32f2f",
          color: "#fff",
        },
      },
      containedGrey: {
        backgroundColor: "#9e9e9e",
        color: "#fff",
        "&:hover": {
          backgroundColor: "#7e7e7e",
        },
      },
      containedWhite: {
        backgroundColor: "#ffffff", // white background
        color: "#000000", // black text for contrast
        "&:hover": {
          backgroundColor: "#f0f0f0", // light grey on hover
        },
      },
    },

    MuiLink: {
      defaultProps: {
        color: "primary",
        underline: "hover",
      },
      styleOverrides: {
        root: {
          cursor: "pointer",
          fontWeight: 500,
        },
      },
    },
  },
});

const darkTheme = createTheme({
  typography: {
    fontFamily: "Arial, sans-serif",
    h1: {
      fontSize: "2.5rem", // 40px
      margin: "0.5em 0",
      [`@media (max-width:600px)`]: {
        fontSize: "2.2rem", // ~33px
      },
    },
    h2: {
      fontSize: "2rem", // 32px
      margin: "0.5em 0",
      [`@media (max-width:600px)`]: {
        fontSize: "1.8rem", // ~27px
      },
    },
    h3: {
      fontSize: "1.75rem", // 28px
      margin: "0.5em 0",
      [`@media (max-width:600px)`]: {
        fontSize: "1.5rem", // ~22.5px
      },
    },
    h4: {
      fontSize: "1.5rem",
      margin: "0.5em 0",
      [`@media (max-width:600px)`]: {
        fontSize: "1.5rem", // ~22.5px
      },
    },
    h5: {
      fontSize: "1rem", //  Module Names
      margin: "0.5em 0",
      [`@media (max-width:600px)`]: {
        fontSize: "1.5rem", // ~22.5px
      },
    },
    h6: {
      fontSize: "0.89rem", //  lesson Names
      margin: "0.5em 0",
      [`@media (max-width:600px)`]: {
        fontSize: "1.5rem", // ~22.5px
      },
    },
    body1: {
      fontSize: "1rem",
    },
    body2: {
      fontSize: "0.875rem", // description and other small fonts
    },
  },

  palette: {
    mode: "dark",
    primary: {
      main: "#9fc5e8", // Custom primary color for dark mode
    },
    secondary: {
      main: "#f50057", // Custom secondary color for dark mode
    },
    grey: {
      main: "#9e9e9e", // or any shade you prefer
      contrastText: "#fff",
    },
    background: {
      default: "#001524", // Dark background color for dark mode
      paper: "#1d1d1d", // Dark paper background color for dark mode
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: "16px",
          "@media (max-width:600px)": {
            fontSize: "15px",
          },
        },
        body: {
          margin: 0,
          padding: 0,
        },
      },
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        color: "primary",
      },
      styleOverrides: {
        root: {
          borderRadius: 3,
          padding: "8px 16px",
          textTransform: "none",
        },
        containedPrimary: {
          backgroundColor: "#1976d2",
          color: "#fff",
        },
        containedSecondary: {
          backgroundColor: "#d32f2f",
          color: "#fff",
        },
      },
      containedGrey: {
        backgroundColor: "#9e9e9e",
        color: "#fff",
        "&:hover": {
          backgroundColor: "#7e7e7e",
        },
      },
    },
    MuiLink: {
      defaultProps: {
        color: "primary",
        underline: "hover",
      },
      styleOverrides: {
        root: {
          cursor: "pointer",
          fontWeight: 500,
        },
      },
    },
  },
});

export { lightTheme, darkTheme };
